import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatbotPersonality } from '../types/chatbot';
import { chatApi } from '../services/chatApi';
import Gallery from './Gallery';

interface ChatbotStats {
  likeCount: number;
  chatCount: number;
  isLiked: boolean;
  hasStartedChat: boolean;
}

interface ConversationSidebarProps {
  chatbot: ChatbotPersonality;
  chatbotStats?: ChatbotStats;
  onUpdateStats: (stats: Partial<ChatbotStats>) => void;
  onClose: () => void;
  onShowProfile: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  chatbot,
  chatbotStats,
  onUpdateStats,
  onClose,
  onShowProfile
}) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    chatInfo: false,
    customizeChat: false,
    mediaFiles: false,
    privacySupport: false
  });
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDeleteChat = async () => {
    if (!window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);

      // Delete chat using companion ID
      await chatApi.deleteChatByCompanion(chatbot.id);

      // Close the sidebar
      onClose();

      // Navigate back to conversations or home
      navigate('/conversations');

    } catch (error) {
      console.error('Failed to delete chat:', error);
      alert('Failed to delete chat. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Get profile image from chatbot data
  const profileImage = chatbot.gallery?.[0] || (chatbot as any).galleryAssets?.[0]?.url;

  // Get media assets for gallery
  const mediaAssets = chatbot.galleryAssets || [];
  const galleryImages = chatbot.gallery || [];
  const allMedia = mediaAssets.length > 0 ? mediaAssets : galleryImages.map(url => ({ url, type: 'image' as const, id: url }));

  if (showMediaGallery) {
    return (
      <div className="h-full bg-dark-800 rounded-2xl border border-dark-600 flex flex-col text-white overflow-hidden shadow-lg">
        {/* Media Gallery Header */}
        <div className="p-4 border-b border-dark-600/30 flex items-center gap-3">
          <button
            onClick={() => setShowMediaGallery(false)}
            className="p-2 text-dark-300 hover:text-white hover:bg-dark-700/30 rounded-xl transition-all duration-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-white">Media and files</h2>
        </div>

        {/* Tabs */}
        <div className="border-b border-dark-600/30">
          <div className="flex">
            <button className="px-4 py-3 text-accent-purple border-b-2 border-accent-purple font-medium transition-all duration-300">
              Media
            </button>
            <button className="px-4 py-3 text-dark-300 hover:text-white transition-all duration-300 hover:bg-dark-700/20 rounded-t-lg">
              Files
            </button>
          </div>
        </div>

        {/* Media Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {allMedia.length > 0 ? (
            <Gallery
              assets={allMedia}
              gridCols={2}
              separateMediaTypes={true}
              initialLoadCount={4}
              loadMoreCount={4}
            />
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📷</div>
              <p className="text-dark-300">No media available</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-dark-800 rounded-2xl border border-dark-600 flex flex-col text-white overflow-hidden shadow-lg">
      {/* Profile Header - Facebook Style */}
      <div className="p-4 text-center">
        {/* Profile Picture */}
        <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden">
          {profileImage ? (
            <img
              src={profileImage}
              alt={chatbot.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent-purple to-accent-pink">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
          )}
        </div>

        {/* Name */}
        <h2 className="text-lg font-semibold mb-1 text-white">{chatbot.name}</h2>

        {/* Active status */}
        <div className="flex items-center justify-center gap-1 mb-3">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          <span className="text-xs text-green-400">Active 1h ago</span>
        </div>

        {/* End-to-end encrypted indicator */}
        <div className="flex items-center justify-center gap-1 text-xs text-dark-300 mb-4">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18,8h-1V6c0-2.76-2.24-5-5-5S7,3.24,7,6v2H6c-1.1,0-2,0.9-2,2v10c0,1.1,0.9,2,2,2h12c1.1,0,2-0.9,2-2V10C20,8.9,19.1,8,18,8z M12,17c-1.1,0-2-0.9-2-2s0.9-2,2-2s2,0.9,2,2S13.1,17,12,17z M15.1,8H8.9V6c0-1.71,1.39-3.1,3.1-3.1s3.1,1.39,3.1,3.1V8z" />
          </svg>
          <span>End-to-end encrypted</span>
        </div>

        {/* Action Buttons - Facebook Style */}
        <div className="flex justify-center gap-4">
          <button
            onClick={onShowProfile}
            className="flex flex-col items-center gap-1 text-dark-300 hover:text-white transition-colors p-2"
          >
            <div className="w-10 h-10 bg-dark-700 hover:bg-dark-600 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span className="text-xs">Profile</span>
          </button>

          <button className="flex flex-col items-center gap-1 text-dark-300 hover:text-white transition-colors p-2">
            <div className="w-10 h-10 bg-dark-700 hover:bg-dark-600 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            </div>
            <span className="text-xs">Mute</span>
          </button>

          <button className="flex flex-col items-center gap-1 text-dark-300 hover:text-white transition-colors p-2">
            <div className="w-10 h-10 bg-dark-700 hover:bg-dark-600 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <span className="text-xs">Search</span>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Chat Info Section */}
        <div className="border-t border-dark-600/30">
          <button
            onClick={() => toggleSection('chatInfo')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-dark-700/30 transition-colors"
          >
            <span className="font-medium text-white text-sm">Chat info</span>
            <svg
              className={`w-4 h-4 transition-transform text-dark-300 ${expandedSections.chatInfo ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSections.chatInfo && (
            <div className="px-4 pb-3">
              <button className="flex items-center gap-3 text-dark-300 hover:text-white transition-colors py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full text-left">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span className="text-sm">View pinned messages</span>
              </button>
            </div>
          )}
        </div>

        {/* Customize Chat Section */}
        <div className="border-t border-dark-600/30">
          <button
            onClick={() => toggleSection('customizeChat')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-dark-700/30 transition-colors"
          >
            <span className="font-medium text-white text-sm">Customize chat</span>
            <svg
              className={`w-4 h-4 transition-transform text-dark-300 ${expandedSections.customizeChat ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSections.customizeChat && (
            <div className="px-4 pb-4 space-y-2">
              <button className="flex items-center gap-3 text-dark-300 hover:text-white transition-all duration-300 py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full text-left">
                <div className="w-5 h-5 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <span className="text-sm">Change theme</span>
              </button>

              <button className="flex items-center gap-3 text-dark-300 hover:text-white transition-all duration-300 py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full text-left">
                <div className="w-5 h-5 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center">
                  <span className="text-xs">👍</span>
                </div>
                <span className="text-sm">Change emoji</span>
              </button>

              <button className="flex items-center gap-3 text-dark-300 hover:text-white transition-all duration-300 py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full text-left">
                <div className="w-5 h-5 flex items-center justify-center">
                  <span className="text-sm font-bold">Aa</span>
                </div>
                <span className="text-sm">Edit nicknames</span>
              </button>
            </div>
          )}
        </div>

        {/* Media & Files Section */}
        <div className="border-t border-dark-600/30">
          <button
            onClick={() => toggleSection('mediaFiles')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-dark-700/30 transition-colors"
          >
            <span className="font-medium text-white text-sm">Media & files</span>
            <svg
              className={`w-4 h-4 transition-transform text-dark-300 ${expandedSections.mediaFiles ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSections.mediaFiles && (
            <div className="px-4 pb-4 space-y-2">
              <button
                onClick={() => setShowMediaGallery(true)}
                className="flex items-center gap-3 text-dark-300 hover:text-white transition-all duration-300 py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full text-left"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2z" />
                </svg>
                <span className="text-sm">Media</span>
              </button>

              <button className="flex items-center gap-3 text-dark-300 hover:text-white transition-all duration-300 py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full text-left">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm">Files</span>
              </button>
            </div>
          )}
        </div>

        {/* Privacy & Support Section */}
        <div className="border-t border-dark-600/30">
          <button
            onClick={() => toggleSection('privacySupport')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-dark-700/30 transition-colors"
          >
            <span className="font-medium text-white text-sm">Privacy & support</span>
            <svg
              className={`w-4 h-4 transition-transform text-dark-300 ${expandedSections.privacySupport ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {expandedSections.privacySupport && (
            <div className="px-4 pb-4 space-y-2">
              <button className="text-left text-dark-300 hover:text-white transition-all duration-300 text-sm py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full">
                Mute notifications
              </button>
              <button className="text-left text-dark-300 hover:text-white transition-all duration-300 text-sm py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full">
                Message permissions
              </button>
              <button className="text-left text-dark-300 hover:text-white transition-all duration-300 text-sm py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full">
                Disappearing messages
              </button>
              <button className="text-left text-dark-300 hover:text-white transition-all duration-300 text-sm py-2 px-2 rounded-lg hover:bg-dark-700/20 w-full">
                Read receipts
              </button>
              <div className="border-t border-dark-600/30 my-2 pt-2">
                <button
                  onClick={handleDeleteChat}
                  disabled={isDeleting}
                  className="text-left text-red-400 hover:text-red-300 transition-all duration-300 text-sm py-2 px-2 rounded-lg hover:bg-red-500/10 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete chat'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Close button */}
      <div className="p-4 border-t border-dark-600/30">
        <button
          onClick={onClose}
          className="w-full py-2 px-4 bg-dark-700/50 hover:bg-dark-600/50 rounded-lg text-sm transition-all duration-300 font-medium text-white"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ConversationSidebar;